import { Box, Button, FormControl, IconButton, styled, Typography } from '@mui/material';
import { XIcon } from 'lucide-react';
import { Statute } from '../../types/Statute.type';
import StatuteAutocomplete from '../StatuteAutocomplete/StatuteAutocomplete';

const StyledSingleBox = styled(Box)({
  backgroundColor: '#fbfcfd',
  padding: '16px',
  borderTop: '1px solid #e0e0e0',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

const StyledSingleBoxContent = styled(Box)({
  background: '#fbfcfd',
  padding: '0px',
  fontSize: '14px',
  fontWeight: 500,
  marginTop: '8px',
  borderRadius: '6px',
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
});

const InputRow = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

interface ComparisonPoolSectionProps {
  type: 'comparator' | 'exclusion';
  cases: number[][];
  countyId: number | null | undefined;
  openAutocompleteId: string | null;
  getStatutesFromIds: (ids: number[]) => Statute[];
  onAddCase: () => void;
  onRemoveCase: (index: number) => void;
  onStatuteChange: (index: number, statutes: Statute[]) => void;
  onCacheUpdate: (statutes: Statute[]) => void;
  onOpenAutocompleteChange: (id: string | null) => void;
  onSelectAllRequest: (caseIndex: number, statutes: Statute[], isDeselecting: boolean) => void;
  disabled?: boolean;
}

function ComparisonPoolSection({
  type,
  cases,
  countyId,
  openAutocompleteId,
  getStatutesFromIds,
  onAddCase,
  onRemoveCase,
  onStatuteChange,
  onCacheUpdate,
  onOpenAutocompleteChange,
  onSelectAllRequest,
  disabled,
}: ComparisonPoolSectionProps) {
  const isComparator = type === 'comparator';
  const title = isComparator ? 'Comparator Pool' : 'Exclusion Pool';
  const buttonLabel = isComparator ? 'Add Comparator Case' : 'Add Exclusion Case';
  const caseLabel = isComparator ? 'Include Case with Statute(s)' : 'Exclude Case with Statute(s)';

  return (
    <StyledSingleBox sx={!isComparator ? { borderLeft: '1px solid #e0e0e0' } : {}}>
      <Typography variant='body1' sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <StyledSingleBoxContent>
        <Button
          variant='contained'
          disableElevation
          sx={{ padding: '10px', maxWidth: '220px' }}
          onClick={onAddCase}
          disabled={!countyId || disabled}
        >
          {buttonLabel}
        </Button>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            marginTop: '36px',
            height: '360px',
            overflowY: 'auto',
          }}
        >
          {cases.map((selectedStatutes, idx) => {
            const autocompleteId = `${type}-${idx}`;
            return (
              <InputRow key={idx}>
                <Typography variant='body2' component='span' sx={{ fontWeight: 500 }}>
                  {caseLabel}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                  <FormControl sx={{ flexGrow: 1 }}>
                    <StatuteAutocomplete
                      countyId={countyId ?? null}
                      value={getStatutesFromIds(selectedStatutes)}
                      onChange={(newValue) => {
                        onCacheUpdate(newValue);
                        onStatuteChange(idx, newValue);
                      }}
                      disabled={false}
                      autocompleteId={autocompleteId}
                      openAutocompleteId={openAutocompleteId}
                      onOpenChange={onOpenAutocompleteChange}
                      onSelectAllRequest={(statutes, isDeselecting) => onSelectAllRequest(idx, statutes, isDeselecting)}
                    />
                  </FormControl>
                  <IconButton onClick={() => onRemoveCase(idx)}>
                    <XIcon size={20} />
                  </IconButton>
                </Box>
              </InputRow>
            );
          })}
        </Box>
      </StyledSingleBoxContent>
    </StyledSingleBox>
  );
}

export default ComparisonPoolSection;
